import React from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import "./shopStyles.css";

const Settings = () => {
  const navigate = useNavigate();
  const currentRole =
    localStorage.getItem("ogestyle_role_override") ||
    localStorage.getItem("ogestyle_role");
  const isR2 = currentRole === "R2";

  const settingsCards = [
    {
      id: "subscription",
      icon: "💎",
      title: "Subscription",
      description: "View your current plan and billing information",
      action: "View",
      color: "#ec4899",
      path: "/dashboard/settings/subscription",
      disabled: false,
    },
    {
      id: "profile",
      icon: "🏢",
      title: "Business Profile",
      description: "Update your business information and social media",
      action: "Configure",
      color: "#3b82f6",
      path: "/dashboard/settings/profile",
      disabled: false,
      r1Only: true,
    },
    {
      id: "admin-profile",
      icon: "👤",
      title: "My Profile",
      description: "Update your personal information and password",
      action: "Configure",
      color: "#3b82f6",
      path: "/dashboard/admin/profile",
      disabled: false,
      r2Only: true,
    },
    {
      id: "professional-profile",
      icon: "✂️",
      title: "Professional Profile",
      description:
        "Enable/disable your professional profile to work at your business",
      action: "Manage",
      color: "#2d6da3",
      path: "/dashboard/settings/professional-profile",
      disabled: false,
    },
    {
      id: "billing",
      icon: "💳",
      title: "Payment Settings",
      description: "Manage Stripe account, billing, and payout settings",
      action: "Configure",
      color: "#8b5cf6",
      path: "/dashboard/settings/billing",
      disabled: false,
    },
    {
      id: "booking",
      icon: "🗓️",
      title: "Booking Settings",
      description: "Configure booking rules and policies",
      action: "Configure",
      color: "#10b981",
      path: "/dashboard/settings/booking",
      disabled: false,
    },
    {
      id: "packages",
      icon: "📦",
      title: "Packages",
      description:
        "Switch packages on and set what each professional is paid per session",
      action: "Configure",
      color: "#6366f1",
      path: "/dashboard/settings/packages",
      disabled: false,
      r1Only: true,
    },
    {
      id: "operations",
      icon: "🏢",
      title: "Business Operations",
      description: "Manage company-wide blocked dates and holidays",
      action: "Configure",
      color: "#06b6d4",
      path: "/dashboard/settings/operations",
      disabled: false,
    },
    {
      id: "notifications",
      icon: "🔔",
      title: "Notifications",
      description: "Control email and notification preferences",
      action: "Configure",
      color: "#f59e0b",
      path: "/dashboard/settings/notifications",
      disabled: false,
    },
    {
      id: "locations",
      icon: "📍",
      title: "Locations",
      description: "View and manage your business locations",
      action: "Configure",
      color: "#ef4444",
      path: "/dashboard/locations",
      disabled: false,
    },
    {
      id: "security",
      icon: "🔒",
      title: "Security",
      description: "Password, 2FA, and account security",
      action: "Configure",
      color: "#6366f1",
      path: "/dashboard/settings/security",
      disabled: false,
    },
  ];

  const visibleSettingsCards = isR2
    ? settingsCards.filter((card) => card.id === "admin-profile")
    : settingsCards.filter((card) => !card.r2Only);

  const handleCardClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <SidebarLayout>
      <div
        className="settings-page"
        style={{
          padding: "24px",
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "700",
              color: "#1a1a1a",
              margin: "0 0 8px 0",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Settings
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#666",
              margin: 0,
              fontFamily: "Inter, sans-serif",
            }}
          >
            Manage your business settings and preferences
          </p>
        </div>

        {/* Settings Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: "24px",
          }}
        >
          {visibleSettingsCards.map((card) => (
            <div
              key={card.id}
              onClick={() => !card.disabled && handleCardClick(card.path)}
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e5e7eb",
                cursor: card.disabled ? "default" : "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                overflow: "hidden",
                opacity: card.disabled ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!card.disabled) {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0, 0, 0, 0.15)";
                }
              }}
              onMouseLeave={(e) => {
                if (!card.disabled) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 1px 3px rgba(0, 0, 0, 0.1)";
                }
              }}
            >
              {/* Color accent bar at top */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: card.color,
                }}
              />

              {/* Icon */}
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: `${card.color}20`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  marginBottom: "16px",
                }}
              >
                {card.icon}
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  margin: "0 0 8px 0",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {card.title}
              </h3>

              {/* Description */}
              <p
                style={{
                  fontSize: "14px",
                  color: "#666",
                  margin: "0 0 16px 0",
                  lineHeight: "1.5",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {card.description}
              </p>

              {/* Action Link */}
              <div
                style={{
                  fontSize: "14px",
                  color: card.color,
                  fontWeight: "500",
                  fontFamily: "Inter, sans-serif",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                {card.action} →
              </div>
            </div>
          ))}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default Settings;
